import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTemplates,
  selectTemplates,
  selectTemplatesStatus,
  selectTemplatesError,
} from "../../../redux/slices/question-template-slice";
import CloneToJobModal from "./CloneToJobModal";

const DIFFICULTY_COLOR = {
  EASY: { bg: "#DCFCE7", fg: "#166534" },
  MEDIUM: { bg: "#FEF9C3", fg: "#854D0E" },
  HARD: { bg: "#FED7AA", fg: "#9A3412" },
  EXPERT: { bg: "#FECACA", fg: "#991B1B" },
};

export default function TemplatesTab() {
  const dispatch = useDispatch();
  const items = useSelector(selectTemplates);
  const status = useSelector(selectTemplatesStatus);
  const error = useSelector(selectTemplatesError);

  const [cloneTarget, setCloneTarget] = useState(null);

  useEffect(() => {
    if (status === "idle") dispatch(fetchTemplates());
  }, [dispatch, status]);

  if (status === "loading") {
    return <div style={emptyState}>Loading templates…</div>;
  }
  if (status === "failed") {
    return (
      <div style={{ ...emptyState, color: "#991B1B" }}>
        {error || "Failed to load templates"}
      </div>
    );
  }
  if (!items.length) {
    return (
      <div style={emptyState}>
        No templates yet. Create one from a question on the "This job" tab to
        seed the bank.
      </div>
    );
  }

  return (
    <div>
      {items.map((t) => {
        const diff = DIFFICULTY_COLOR[t.difficulty] || DIFFICULTY_COLOR.MEDIUM;
        return (
          <div key={t.templateId} style={card}>
            <div style={cardMain}>
              <div style={cardName}>{t.name || "(unnamed template)"}</div>
              <div style={cardText}>{t.questionText}</div>
              <div style={chipRow}>
                {t.questionType && <span style={typePill}>{t.questionType}</span>}
                {t.difficulty && (
                  <span style={{ ...diffPill, background: diff.bg, color: diff.fg }}>
                    {t.difficulty}
                  </span>
                )}
                {Array.isArray(t.tags) &&
                  t.tags.map((tag) => (
                    <span key={tag} style={tagPill}>
                      {tag}
                    </span>
                  ))}
              </div>
            </div>
            <button onClick={() => setCloneTarget(t)} style={cloneBtn}>
              Clone to job →
            </button>
          </div>
        );
      })}

      <CloneToJobModal
        open={!!cloneTarget}
        template={cloneTarget}
        onClose={() => setCloneTarget(null)}
        onCloned={() => setCloneTarget(null)}
      />
    </div>
  );
}

const emptyState = {
  padding: 40,
  textAlign: "center",
  color: "#94A3B8",
  background: "white",
  border: "1px dashed #E2E8F0",
  borderRadius: 12,
  fontSize: 13,
};
const card = {
  background: "white",
  border: "1px solid #E2E8F0",
  borderRadius: 7,
  padding: "12px 14px",
  marginBottom: 8,
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
};
const cardMain = { flex: 1, minWidth: 0 };
const cardName = {
  fontSize: 12,
  fontWeight: 700,
  color: "#0F172A",
  marginBottom: 2,
};
const cardText = {
  fontSize: 13,
  color: "#334155",
  lineHeight: 1.4,
  marginBottom: 8,
};
const chipRow = { display: "flex", gap: 6, flexWrap: "wrap" };
const typePill = {
  background: "#EEF2FF",
  color: "#4338CA",
  padding: "2px 8px",
  borderRadius: 100,
  fontSize: 10,
  fontWeight: 600,
};
const diffPill = {
  padding: "2px 8px",
  borderRadius: 100,
  fontSize: 10,
  fontWeight: 600,
};
const tagPill = {
  background: "#F1F5F9",
  color: "#475569",
  padding: "2px 7px",
  borderRadius: 100,
  fontSize: 10,
  fontWeight: 600,
};
const cloneBtn = {
  padding: "6px 12px",
  borderRadius: 6,
  background: "white",
  color: "#4338CA",
  border: "1px solid #C7D2FE",
  fontWeight: 600,
  fontSize: 11,
  cursor: "pointer",
  whiteSpace: "nowrap",
};
